import moment from 'moment'
import i18n from '@/i18n/i18nMain'
import store from '@/store/mainStore'
import { uiToastsEnabled } from '@/utils/uiToasts'

/**
 * Pure utility functions extracted from the 487-line global mixin formerly
 * registered in `src/main.js`. The mixin still installs (so existing
 * Options API components keep accessing these via `this.X`), but its
 * methods now delegate to the pure functions defined here.
 *
 * Two motivations:
 * 1. **Tree-shake-friendly** — components that only use one helper no
 *    longer pull the whole mixin's dependency graph.
 * 2. **Composable-ready** — `src/composables/useGlobals.js` re-exports
 *    these for `<script setup>` consumers (Phase 7 will adopt that as
 *    files are converted; until then the mixin remains the canonical
 *    Options-API surface).
 *
 * Behaviour preserved 1:1 with the legacy mixin methods. Where a mixin
 * method read `this.$t` / `this.$snotify` / `store.state.X`, those are
 * now accessed through directly-imported singletons (`i18n`, `store`)
 * or passed in as arguments (`spawn*Toast(toast, message, ...)`).
 *
 * Date utilities that already live in `format.js` are NOT re-exported
 * here — import from there directly. This file owns the rest.
 */

// ─── Object / array ──────────────────────────────────────────────────

export function deepCopy(objectOrArray) {
    return JSON.parse(JSON.stringify(objectOrArray))
}

export function getArrayDifference(fullArray, subset) {
    const all = new Set(fullArray)
    const used = new Set(subset)
    const setDifference = (a, b) => new Set([...a].filter(x => !b.has(x)))
    return [...setDifference(all, used)]
}

// ─── Date / time ──────────────────────────────────────────────────────

export function transformMessageDate(value) {
    if (typeof value !== 'string' || /^[0-9]+$/.test(value)) {
        value = +value * 1000
    }
    if (!value) return ''
    const formatedDateTime = moment.utc(value).local().format('YYYY-MM-DD HH:mm')
    const formatedDate = moment.utc(value).local().format('YYYY-MM-DD')
    const daysFromNow = moment().diff(formatedDate, 'days')
    let dateText = ''
    if (daysFromNow === 0) {
        const secondsFromNow = moment().diff(formatedDateTime, 'second')
        if (secondsFromNow < 200) return i18n.global ? i18n.global.t('Now') : i18n.t('Now')
        dateText = i18n.global ? i18n.global.t('Today') : i18n.t('Today')
    }
    if (daysFromNow === 1) {
        dateText = i18n.global ? i18n.global.t('Yesterday') : i18n.t('Yesterday')
    }
    let result
    if (store.state.userStore.user_settings.language === 'da') {
        result = moment.utc(value).local().format('DD/MM/YYYY HH:mm')
    } else {
        result = formatedDateTime
    }
    if (dateText) {
        const hours = moment(formatedDateTime).format('HH:mm')
        return `${dateText} ${hours}`
    }
    return result
}

export function dateAddDays(date, days) {
    if (!date) return ''
    const ms = date * 1000
    const convertedDate = moment.utc(ms).local().format('YYYY-MM-DD')
    if (store.state.userStore.user_settings.language === 'da') {
        return moment(convertedDate).add('days', days).format('DD/MM/YYYY')
    }
    return moment(convertedDate).add('days', days).format('YYYY-MM-DD')
}

export function formatToDate(value, language) {
    if (!value) return ''
    if (
        (typeof value !== 'string' || /^[0-9]+$/.test(value)) &&
        value.toString().length < 13
    ) {
        value = +value * 1000
    }
    if (!value) return ''
    if (store.state.userStore.user_settings.language === 'da' || language === 'da') {
        return moment.utc(value).local().format('DD/MM/YYYY')
    }
    return moment.utc(value).local().format('YYYY-MM-DD')
}

export function formatUnixTimestampToLL(value) {
    if (!value) return ''
    if (
        (typeof value !== 'string' || /^[0-9]+$/.test(value)) &&
        value.toString().length < 13
    ) {
        value = +value * 1000
    }
    if (!value) return ''
    return moment.utc(value).local().format('ll')
}

export function formatDateAndTimeForDatepicker(value) {
    if (!value) return 0
    return moment.utc(+value * 1000).local().format('YYYY-MM-DD')
}

export function formatDateAndTimeForBackend(value) {
    if (!value) return 0
    return moment.utc(value).local().format('YYYY-MM-DD HH:mm')
}

export function getTimestampTime(value) {
    if (!value) return 0
    return moment.utc(+value * 1000).local().format('HH:mm:ss')
}

export function convertDateTimeToUtc(value, options = {}) {
    const returnFormat = options.returnFormat || 'YYYY-MM-DD HH:mm:ss'
    const returnTimestamp = options.returnTimestamp || false
    const returnTimestampInSeconds = options.returnTimestampInSeconds || false

    const dateTime = value instanceof moment
        ? moment(value.toDate().getTime())
        : moment(value)

    if (returnTimestamp) {
        return returnTimestampInSeconds
            ? Math.floor(dateTime.toDate().getTime() / 1000)
            : dateTime.toDate().getTime()
    }
    return dateTime.utc().format(returnFormat)
}

// ─── String utilities ─────────────────────────────────────────────────

export function capitalizeFirstLetter(str) {
    if (!str || !str.length) return str
    return str.charAt(0).toUpperCase() + str.slice(1)
}

export function copyToClipboard(str) {
    const el = document.createElement('textarea')
    el.value = str
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
}

export function generateRandomString() {
    return (Math.random() + 1).toString(36).substring(2, 14)
}

export function newlineToBR(str) {
    return str.replace(/(?:\r\n|\r|\n)/g, '<br>')
}

export function stripHtml(html) {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
}

export function formatHtml(html) {
    // remove script tags (legacy regex preserved verbatim)
    return html.replace(
        '<script(?:(?!\\/\\/)(?!\\/\\*)[^\'"]|"(?:\\\\.|[^"\\\\])*"|\'(?:\\\\.|[^\'\\\\])*\'|\\/\\/.*(?:\\n)|\\/\\*(?:(?:.|\\s))*?\\*\\/)*?<\\/script>',
        '',
    )
}

export function removeNewlines(string) {
    return string
        .replace(/\r?\n|\r/g, '')
        .replace(/&lt;br&gt;/g, '')
        .replace(/&nbsp;/gi, '')
}

export function stripYoutubeIdFromURL(url) {
    const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|\&v=)([^#\&\?]*).*/
    const match = url.match(regExp)
    if (match && match[2].length === 11) return match[2]
    return ''
}

export function testIsUrl(str) {
    const pattern = new RegExp(
        '^(https?:\\/\\/)?' +
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' +
        '((\\d{1,3}\\.){3}\\d{1,3}))' +
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
        '(\\?[;&a-z\\d%_.~+=-]*)?' +
        '(\\#[-a-z\\d_]*)?$',
        'i',
    )
    return pattern.test(str)
}

export function testIsEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(email)
}

export function generateLinkFromURL(inputText) {
    const replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim
    const replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim
    const replacePattern3 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim
    return inputText
        .replace(replacePattern1, '<a href="$1" target="_blank">$1</a>')
        .replace(replacePattern2, '$1<a href="http://$2" target="_blank">$2</a>')
        .replace(replacePattern3, '<a href="mailto:$1">$1</a>')
}

// ─── User picture / avatar ────────────────────────────────────────────

export function getUserDefaultPicture(userId) {
    if (!userId) return 0
    const bgColors = [
        '#6E44FF', '#35A7FF', '#FF5964', '#FF9466', '#1E3888', '#00CC66',
        '#020402', '#108D82', '#F4D507', '#868282', '#62DBB5',
    ]
    const sum = String(userId).split('').map(Number).reduce((a, b) => a + b, 0)
    const reminder = (sum / bgColors.length).toFixed(1)
    return bgColors[Math.ceil((reminder % 1) * 10)] || '#62DBB5'
}

// ─── Toast spawners ────────────────────────────────────────────────────
// `toast` is the $snotify-shaped object. The mixin methods pass `this.$snotify`;
// the composable passes the value resolved via globalProperties.

export function spawnSuccessToast(toast, message, timeToLive = 1500, title = null) {
    if (!toast) return
    // Opt-out per user, same switch as the LearnUToast confirmations.
    if (!uiToastsEnabled(store.state.userStore.user_settings)) return
    const t = title || (i18n.global ? i18n.global.t('Success') : i18n.t('Success'))
    toast.success(message || '', t, {
        position: 'leftBottom',
        timeout: timeToLive || 1500,
        showProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        color: 'completedGreen',
    })
}

export function spawnErrorToast(toast, message, timeToLive = 1500) {
    if (!toast) return
    const t = i18n.global ? i18n.global.t('Error') : i18n.t('Error')
    toast.error(message || '', t, {
        position: 'leftBottom',
        timeout: timeToLive || 1500,
        showProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
    })
}

// ─── Tracking / analytics ─────────────────────────────────────────────

export function trackEvent({ name, options = {}, properties = {}, user = {} }) {
    if (!window.EncTracking) return 0
    try {
        window.EncTracking.track({
            name,
            properties: { ...properties, date: new Date() },
            user: {
                email: store.state.userStore.user.email || '',
                firstName: store.state.userStore.user.firstname || '',
                lastName: store.state.userStore.user.lastname || '',
                institutionType: store.state.userStore.default_institution?.client?.client_type || '',
                ...user,
            },
            ...options,
        })
    } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
    }
    return 1
}

// ─── Static defaults ──────────────────────────────────────────────────

// Used by FileModel/folderTree/etc. to build URLs from
// `media.converted_handle` (and the legacy `file_service_handle`).
// Flipped 2026-05-06 from cdn.filestackcontent.com to files.edaptio.com
// because converted_handle now holds an S3 key (`converted/{uuid}.pdf`)
// rather than a Filestack handle. Pre-cutover Filestack-converted PDFs
// (rows whose converted_handle is a short opaque handle, not an
// S3-key path) will 404 here — the original (non-converted) cdn_url
// still resolves and FileModel's `if (this.fileIsConverted)` guard
// degrades gracefully.
export const DEFAULT_CDN_PREFIX = 'https://files.edaptio.com'
export const FILESTACK_CDN_PREFIX = 'https://cdn.filestackcontent.com'

// Resolve a media row's ORIGINAL-file URL. Slim-era prod rows carry BOTH a
// Filestack `file_service_handle` AND an S3 `cdn_url`; preferring the handle
// (the pre-2026-07-10 behavior) glued a Filestack handle onto
// files.edaptio.com and returned S3 AccessDenied XML for ~1.29M rows the
// moment prod cut over. `cdn_url` is authoritative when present; the bare
// handle (427 rows with no cdn_url) still lives on Filestack's CDN.
export function mediaFileUrl(file = {}) {
    const cdnUrl = file.cdn_url || ''
    if (cdnUrl.length) return cdnUrl
    // The bare `file_service_handle` branch used to fall back to Filestack's
    // CDN. The subscription lapsed ~2026-07-30 and every handle now answers
    // `429 application ... is unavailable`, so that URL can only ever render
    // as broken. Prod has 0 rows with a handle and no cdn_url, so nothing
    // regresses by dropping it.
    return ''
}

// Resolve a media row's EMBED (converted) URL. Post-Filestack conversions
// store an S3 key (`converted/{uuid}.pdf`, has a slash) served from
// files.edaptio.com; Slim-era conversions store a bare Filestack handle
// (no slash) that only ever resolved on Filestack's CDN.
//
// Filestack is gone, so a bare handle is now a DEAD preview — 45,254 prod
// rows still hold one because `filestack:reconvert` was switched off
// (FILESTACK_RECONVERT_SHARDS=0) before it drained. All 45,254 have their
// ORIGINAL on our S3, so fall back to that: the user gets the real file
// instead of a broken embed, and the reconvert backfill can still replace
// it with a proper PDF preview later.
export function mediaEmbedUrl(info = {}) {
    const converted = (info.converted_handle || '').trim()
    if (converted.includes('/') && !parseInt(info.convert_error)) {
        return `${DEFAULT_CDN_PREFIX}/${converted}`
    }
    return mediaFileUrl(info)
}
export const DEFAULT_PROFILE_PICTURE_URL = 'https://images.edaptio.com/user-placeholder-pic.png'

export function defaultErrorMessages() {
    const t = (k) => (i18n.global ? i18n.global.t(k) : i18n.t(k))
    return {
        invalidRange: ({ min, max }) => `${t('Must be between')} ${min} - ${max}`,
        invalidType: ({ type = '' }) => `${t('Invalid')} ${type}`,
        required: () => t('Required'),
        tooFewType: ({ min, ofType = '' }) => `${t('Requires at least')} ${min} ${ofType}`,
    }
}

export function isProductionEnv() {
    return import.meta.env.MODE === 'production'
}

export function isStagingEnv() {
    return import.meta.env.MODE === 'staging'
}

export function currentEnvironmentBaseURL() {
    if (isProductionEnv()) return 'https://app.edaptio.com'
    if (isStagingEnv()) return 'https://stg.edaptio.com'
    return 'http://localhost:8080'
}

// ─── Device-size getters (Vuex-backed, computed-friendly) ─────────────

export function isExtraLargeDevice() {
    return store.state.generalStore.device === 'laptop' || false
}

export function isLargeDevice() {
    return store.state.generalStore.device === 'laptop' || false
}

export function isMediumDevice() {
    return store.state.generalStore.device === 'tablet' || false
}

export function isSmallDevice() {
    return store.state.generalStore.device === 'mobile' || false
}

export function getUserLanguage() {
    if (
        store.state.userStore &&
        store.state.userStore.user_settings &&
        store.state.userStore.user_settings.language
    ) {
        return store.state.userStore.user_settings.language
    }
    if (!i18n.global) return i18n.locale || 'en'
    return i18n.global.locale || 'en'
}
